// Design tokens, mirrored from the Tailwind theme defined in /index.css
// (@theme block). CSS is the source of truth for styling; this module exists
// so stories and JS code (e.g. useMediaQuery) can read the same values.
// Keep both files in sync when a token changes.

export const screens = {
  sm: "640px",
  md: "1024px",
  lg: "1280px",
} as const;

export const colors = {
  transparent: "transparent",
  white: "#FFFFFF",
  red: "#FF5F49",
  grey: {
    1: "#F5F5F5",
    2: "#F2F2F2",
    3: "#E4E4E4",
    4: "#D7D7D7",
    5: "#C5C5C5",
    6: "#A9A9A9",
    7: "#888888",
    8: "#6A6A6A",
    9: "#525252",
    10: "#3A3A3A",
    11: "#2A2A2A",
    12: "#242424",
  },
  black: {
    DEFAULT: "#1E1E1E",
    1: "#151515",
    0: "#000000",
  },
  cream: "#F4F0ED",
  nude: {
    DEFAULT: "#FFE6D5",
    1: "#FFF3EF",
  },
  peach: {
    DEFAULT: "#FFB07C",
    soft: "#FFB097",
  },
  tangerine: {
    DEFAULT: "#FF8762",
    1: "#FFA184",
  },
  yellow: {
    DEFAULT: "#FFCC63",
    light: "#FFF1D8",
    "light-1": "#FFFAEF",
  },
  green: {
    DEFAULT: "#35BD6E",
    secondary: "#86DAC8",
    light: "#C3F5EB",
    "light-1": "#F3FBF9",
  },
  blue: {
    DEFAULT: "#6FA1F7",
    1: "#A9C9FF",
    2: "#8EB8FF",
    light: "#D8E2FF",
    "light-1": "#F1F6FE",
    "light-2": "#EFF3FF",
  },
};

export const borderWidth = {
  0: "0px",
  1: "0.5px",
  2: "1px",
  3: "1.5px",
  4: "2px",
};

export const borderRadius = {
  0: "0px",
  1: "0.25rem",
  2: "0.5rem",
  3: "0.75rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "2rem",
  8: "3rem",
  9: "4rem",
  full: "9999px",
};

export const boxShadow = {
  "y-plus-1": "0px 8px 8px 0px rgba(0, 0, 0, 0.1)",
  "y-zero-1": "0px 0px 8px 0px rgba(0, 0, 0, 0.1)",
  "y-minus-1": "0px -8px 8px 0px rgba(0, 0, 0, 0.1)",
  "y-plus-2": "0px 8px 16px 0px rgba(0, 0, 0, 0.1)",
  "y-zero-2": "0px 0px 16px 0px rgba(0, 0, 0, 0.1)",
  "y-minus-2": "0px -8px 16px 0px rgba(0, 0, 0, 0.1)",
  "y-plus-3": "0px 8px 24px 0px rgba(0, 0, 0, 0.1)",
  "y-zero-3": "0px 0px 24px 0px rgba(0, 0, 0, 0.1)",
  "y-minus-3": "0px -8px 24px 0px rgba(0, 0, 0, 0.1)",
  "y-plus-4": "0px 8px 32px 0px rgba(0, 0, 0, 0.1)",
  "y-zero-4": "0px 0px 32px 0px rgba(0, 0, 0, 0.1)",
  "y-minus-4": "0px -8px 32px 0px rgba(0, 0, 0, 0.1)",
  "y-plus-5": "0px 8px 40px 0px rgba(0, 0, 0, 0.1)",
  "y-zero-5": "0px 0px 40px 0px rgba(0, 0, 0, 0.1)",
  "y-minus-5": "0px -8px 40px 0px rgba(0, 0, 0, 0.1)",
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
};

export const fontFamily = {
  sans: ["Matter"],
};

export const fontSize = {
  "5xl": "2.5rem",
  "4xl": "2rem",
  "3xl": "1.75rem",
  "2xl": "1.5rem",
  xl: "1.25rem",
  lg: "1.125rem",
  base: "1rem",
  md: "0.938rem",
  sm: "0.875rem",
  xs: "0.813rem",
  "2xs": "0.75rem",
  "3xs": "0.688rem",
  "4xs": "0.625rem",
  "5xs": "0.563rem",
};

export const lineHeight = {
  1: "0.563rem",
  2: "0.625rem",
  3: "0.688rem",
  4: "0.75rem",
  5: "0.813rem",
  6: "0.875rem",
  7: "1rem",
  8: "1.125rem",
  9: "1.188rem",
  10: "1.25rem",
  11: "1.313rem",
  12: "1.5rem",
  13: "1.75rem",
  14: "2rem",
  15: "2.5rem",
  none: "1",
};

export const letterSpacing = {
  tightest: "-0.02em",
  normal: "0",
  wide: "0.5px",
  wider: "0.01em",
  widest: "0.02em",
};
